import styled from '@emotion/styled';
import { Card, TextField, Typography } from '@material-ui/core';
import firebase from 'firebase/app';
import * as firebaseui from 'firebaseui';
import 'firebaseui/dist/firebaseui.css';
import { useEffect, useState } from 'react';
import {
  ButtonLoader,
  ButtonWithPromise
} from '../../components/ButtonWithPromise';
import { Form } from '../../components/Form';
import { FormField } from '../../components/Form/FormField';
import { FormRow } from '../../components/Form/FormRow';
import { FormSubmitButton } from '../../components/Form/FormSubmitButton';
import { Loader } from '../../components/Loader';
import { useAfterFirstRender } from '../../hooks/useAfterFirstRender';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../i18next';
import { Centered } from '../../layout/Centered';
import { FlexContainer } from '../../layout/Flex';
import { TopNavBarLogoOnly } from '../../layout/TopNav';
import { useRoutes } from '../../routes';
import { signInWithEmailAndPassword } from '../../services/auth';
import { auth } from '../../services/firebase';
import { callFirebaseFunction } from '../../services/firebaseFunctions';
import { LightMdToJsx } from '../../services/formatter';

const msProvider = new firebase.auth.OAuthProvider('microsoft.com');
msProvider.setCustomParameters({
  prompt: 'select_account'
});

// msProvider.setCustomParameters({
//   redirect_uri: 'https://emplexp-dev.firebaseapp.com/__/auth/handler'
// });
const CONFIG: firebaseui.auth.Config = {
  signInFlow: 'popup',
  signInOptions: [
    {
      provider: firebase.auth.GoogleAuthProvider.PROVIDER_ID,
      customParameters: {
        // Forces account selection even when one account
        // is available.
        prompt: 'select_account'
      }
    },
    {
      provider: msProvider.providerId,
      customParameters: {
        prompt: 'select_account'
      }
    },
    // firebase.auth.FacebookAuthProvider.PROVIDER_ID,
    // firebase.auth.TwitterAuthProvider.PROVIDER_ID,
    firebase.auth.EmailAuthProvider.PROVIDER_ID
  ],
  callbacks: {
    signInSuccessWithAuthResult: () => {
      return false;
    }
  }
};

const ID = 'firebase-ui-login-container';

let ui: firebaseui.auth.AuthUI;

const LoginPanel = () => {
  useAfterFirstRender(() => {
    firebase
      .auth()
      .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      .then(() => {
        // we might not be on this screen anymore, but we have also
        // no way to cancel to promise!
        if (document.getElementById(ID)) {
          // this is generally not needed, but useful in development, where fast-refresh
          // might otherwise initialize this again, which leads to an error
          if (import.meta.env.DEV) {
            try {
              if (!ui) {
                ui = new firebaseui.auth.AuthUI(firebase.auth());
              }
              ui.start(`#${ID}`, CONFIG);
            } catch {}
          } else {
            if (!ui) {
              ui = new firebaseui.auth.AuthUI(firebase.auth());
            }
            ui.start(`#${ID}`, CONFIG);
          }
        }
      });
  });
  return <div id={ID} />;
};
const LoginWrapper = styled('div')`
  height: 100vh;
  width: 100%;
`;

export const Login = () => {
  return (
    <LoginWrapper>
      <TopNavBarLogoOnly />
      <Centered height={300}>
        <LoginPanel />
      </Centered>
    </LoginWrapper>
  );
};

const Container = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  minHeight: '100%',

  textAlign: 'center',
  gap: p.theme.spacing()
}));

const DadoLogoContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(1)
}));

const HeadlineContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(3)
}));

const MSButton = styled('img')((p) => ({
  cursor: 'pointer'
}));

export const LoginWithSsoCheck = () => {
  const { t } = useUnsafeTranslation();
  const { getQuery } = useRoutes();
  const emailFromQuery = (getQuery().email as string) || '';
  const [askForPassword, setAskForPassword] = useState(false);
  const [useMicrosoftSSO, setUseMicrosoftSSO] = useState(false);
  const [checkingLoginMethod, setCheckingLoginMethod] = useState(
    !!emailFromQuery
  );

  const [errors, setErrors] = useState<{
    email: string;
    password: string | React.ReactNode;
  }>({
    email: '',
    password: ''
  });

  const { enqueueSnackbar } = useSnackbar();

  const handleMS = async () => {
    await auth().signInWithRedirect(msProvider);
  };

  useEffect(() => {
    if (!emailFromQuery) return;
    callFirebaseFunction('adminUser-getLoginMethod', {
      email: emailFromQuery.toLowerCase(),
      currentUrl: window.location.href
    })
      .then((res: any) => {
        if (res?.ok) {
          if (res?.requiredMethod === 'SSO') {
            window.location.href = res?.loginUrl;
            return;
          }
          if (res?.requiredMethod === 'MICROSOFT_SSO') {
            setUseMicrosoftSSO(true);
          }
          if (res?.requiredMethod === 'PASSWORD') {
            setAskForPassword(true);
          }
        }
        setCheckingLoginMethod(false);
      })
      .catch(() => {
        setCheckingLoginMethod(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (checkingLoginMethod) {
    return (
      <LoginWrapper>
        <Container>
          <DadoLogoContainer>
            <img
              src={'/images/dado-logo/dado-logo.svg'}
              height={48}
              alt="Dado"
            />
          </DadoLogoContainer>
          <Loader height={200} />
        </Container>
      </LoginWrapper>
    );
  }

  return (
    <LoginWrapper>
      <Container>
        <DadoLogoContainer>
          <img src={'/images/dado-logo/dado-logo.svg'} height={48} alt="Dado" />
        </DadoLogoContainer>
        <Card style={{ minWidth: 400 }}>
          <HeadlineContainer>
            <Typography variant="h3">{t('Welcome back!')}</Typography>
          </HeadlineContainer>

          <Form<{ email: string; password: string }>
            initialValues={{ email: emailFromQuery, password: '' }}
            onSubmit={async (vs) => {
              setErrors({ email: '', password: '' });
              if (askForPassword) {
                try {
                  await signInWithEmailAndPassword(
                    vs.email.toLowerCase(),
                    vs.password
                  );
                } catch (err: any) {
                  if (
                    err?.code === 'auth/internal-error' &&
                    err?.message.includes('Missing password requirements')
                  ) {
                    setErrors((x) => ({
                      ...x,
                      password: (
                        <div>
                          <div>
                            {t(
                              'Your password does not meet our updated security requirements.'
                            )}
                          </div>
                          <div>
                            {t(
                              'Please reset and choose a more secure password.'
                            )}
                          </div>
                        </div>
                      )
                    }));
                    return;
                  }
                  // By default, we show this generic error message only,
                  // so that no user enumeration is possible.
                  setErrors((x) => ({
                    ...x,
                    password: 'Please enter the correct password'
                  }));
                }
              } else {
                try {
                  const res: any = await callFirebaseFunction(
                    'adminUser-getLoginMethod',
                    {
                      email: vs.email.toLowerCase(),
                      currentUrl: window.location.href
                    }
                  );
                  console.log('loginMethod', res);
                  if (res?.ok) {
                    if (res?.requiredMethod === 'SSO') {
                      window.location.href = res?.loginUrl;
                    }
                    if (res?.requiredMethod === 'PASSWORD') {
                      setAskForPassword(true);
                      return;
                    }

                    if (res?.requiredMethod === 'MICROSOFT_SSO') {
                      setUseMicrosoftSSO(true);
                      return;
                    }
                  }
                  if (!res?.ok && res?.error === 'no-org') {
                    setErrors((x) => ({
                      ...x,
                      email: t(
                        'Sorry, this user is not part of any organization'
                      )
                    }));
                  }
                  return null;
                } catch (err: any) {
                  setErrors((x) => ({
                    ...x,
                    email: t('Something went wrong')
                  }));
                  return null;
                }
              }
            }}
          >
            {({ submitting, values }) => (
              <>
                {!useMicrosoftSSO && (
                  <FormRow
                    style={{ display: askForPassword ? 'none' : 'auto' }}
                  >
                    <FormField name="email">
                      {({ input }) => (
                        <TextField
                          {...input}
                          variant="outlined"
                          label={t('Email address')}
                          type="email"
                          disabled={askForPassword}
                          autoComplete="username"
                          error={!!errors.email}
                          helperText={errors.email}
                          fullWidth
                        />
                      )}
                    </FormField>
                  </FormRow>
                )}
                {askForPassword && (
                  <FormRow>
                    <FormField name="password">
                      {({ input }) => (
                        <TextField
                          {...input}
                          variant="outlined"
                          type="password"
                          autoComplete="current-password"
                          label={t('Password')}
                          error={!!errors.password}
                          helperText={errors.password}
                          fullWidth
                        />
                      )}
                    </FormField>
                  </FormRow>
                )}
                {askForPassword ? (
                  <>
                    <FormRow>
                      <FormSubmitButton
                        submitting={submitting}
                        submitComponent={
                          <>
                            <ButtonLoader /> {t('Signing in...')}
                          </>
                        }
                        disabled={!values.email || !values.password}
                        fullWidth
                      >
                        {t('Sign in')}
                      </FormSubmitButton>
                    </FormRow>
                    {!!errors.password && (
                      <ButtonWithPromise
                        pending={`${t('Resetting password')}...`}
                        variant="text"
                        color="primary"
                        size="small"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          ev.preventDefault();
                          return auth()
                            .sendPasswordResetEmail(values.email)
                            .then(() =>
                              enqueueSnackbar(
                                t(
                                  'Please check your inbox and spam folder for an email containing a reset password link.'
                                ),
                                { variant: 'success' }
                              )
                            )
                            .catch((err) => {
                              if (err?.code === 'auth/user-not-found') {
                                enqueueSnackbar(
                                  t(
                                    'Please check your inbox and spam folder for an email containing a reset password link.'
                                  ),
                                  { variant: 'success' }
                                );
                              } else {
                                return enqueueSnackbar(
                                  t(
                                    'We were unable to send your Password Reset email to {{ email }}. Please contact support.',
                                    {
                                      email: values.email
                                    }
                                  ),
                                  { variant: 'error' }
                                );
                              }
                            });
                        }}
                      >
                        {t('Reset password')}
                      </ButtonWithPromise>
                    )}
                  </>
                ) : useMicrosoftSSO ? (
                  <FlexContainer
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <MSButton
                      src={'/images/integrations-logos/microsoft/login_ms.svg'}
                      onClick={handleMS}
                    />
                  </FlexContainer>
                ) : (
                  <FormRow>
                    <FormSubmitButton
                      submitting={submitting}
                      submitComponent={
                        <>
                          <ButtonLoader /> {t('Signing in...')}
                        </>
                      }
                      disabled={!values.email}
                      fullWidth
                    >
                      {t('Sign in')}
                    </FormSubmitButton>
                  </FormRow>
                )}
              </>
            )}
          </Form>
        </Card>
      </Container>
    </LoginWrapper>
  );
};

export const ExchangeTokens = () => {
  // hold error states and display something meaningful
  const { getQuery } = useRoutes();
  useEffect(() => {
    fetch(
      `/api/token/exchange?email=${encodeURIComponent(
        getQuery().email as string
      )}`,
      {
        headers: {
          Accept: 'plain/text'
        },
        credentials: 'include'
      }
    )
      .then(async (res) => res.text())
      .then((customToken) => {
        return auth()
          .signInWithCustomToken(customToken)
          .then(() => {
            window.location.href = (getQuery().redirect as string) || '/';
          });
      })
      .catch((err) => {
        console.log('Authentication error', err);
      });
  });
  return <Loader height={500} />;
};

export const SsoLoginTest = () => {
  const { t } = useUnsafeTranslation();
  const [session, setSession] = useState<{
    email: string;
    type: 'USER' | 'ADMIN';
  } | null>(null);

  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`/api/sso/test-session`, {
      headers: {
        Accept: 'application/json'
      },
      credentials: 'include'
    })
      .then(async (res) => res.json())
      .then((session: { email: string; type: 'USER' | 'ADMIN' }) => {
        setSession(session);
      })
      .catch((err) => {
        console.log('Authentication error', err);
        setError(true);
      });
  }, []);
  if (error) {
    return (
      <LoginWrapper>
        <TopNavBarLogoOnly />
        <Container>
          <Typography component="h3" variant="h3">
            {t('SSO test failed!')}
          </Typography>
          <p>{t(`Please try again or contact support.`)}</p>
        </Container>
      </LoginWrapper>
    );
  }
  if (session) {
    return (
      <LoginWrapper>
        <TopNavBarLogoOnly />
        <Container>
          <Typography component="h3" variant="h3">
            {t('SSO test successful!')}
          </Typography>
          <LightMdToJsx component="p">
            {t(`We've identified you as **{{ email }}**.`, {
              email: session.email
            })}
          </LightMdToJsx>
          <div>{t('You can leave this page now and return to Dado.')}</div>
        </Container>
      </LoginWrapper>
    );
  }
  return <Loader height={500} />;
};
